import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { BookCheck, Blocks, LayoutDashboard, MessageCircle } from "lucide-react";

import { ItemType, SideBarItem } from "@/components/SideBar";
import { BRAND } from "@/brand/brand";
import { useCustomerLogo } from "@/hooks/useCustomerLogo";
import { cn } from "@/lib/utils";
import { CBOS_ENTRY_PATH, showsCbosEntry } from "@/cbos/cbosAccess";
import { authenticationAtom } from "../State";
import { myPlatformRolesAtom } from "../PlatformRolesAPI";
import { RULEBOOK_PATH } from "../rulebook/rulebookPath";
import {
  AnimatedSidebar,
  AnimatedSidebarClose,
  AnimatedSidebarContent,
  AnimatedSidebarGroup,
  AnimatedSidebarGroupContent,
  AnimatedSidebarGroupLabel,
  AnimatedSidebarHeader,
  AnimatedSidebarMenu,
  AnimatedSidebarMenuButton,
  AnimatedSidebarMenuItem,
  AnimatedSidebarMenuSub,
  AnimatedSidebarMenuSubButton,
  AnimatedSidebarMenuSubItem,
  type AnimatedSidebarProps,
  useAnimatedSidebar,
} from "@/components/motion/animated-sidebar";
import { SidebarCollapseIcon } from "@/components/ui/sidebar";

interface Properties {
  /** Gleiche Items-Struktur wie die bisherige SideBar (Headings bilden Gruppen). */
  items: SideBarItem[];
  /** Aktiver Eintrag, z. B. aus dem ?panel=-Parameter. */
  selectedKey?: string;
  setItem?: (item: string) => void;
  /**
   * Über der Bereichsliste, für eine Auswahl, die für alle Bereiche eines Gebiets gilt.
   *
   * /cbos wählt so sein Projekt: Es bestimmt, was jeder Bildschirm darunter zeigt, gehört also
   * keinem von ihnen. In einen Bereich gestellt läse es sich als dessen Einstellung, und der
   * Betrachter dürfte sich fragen, was der andere Bereich gerade zeigt.
   */
  aboveGroups?: React.ReactNode;
}

interface SideBarGroupData {
  key: string;
  label?: String;
  items: SideBarItem[];
}

/**
 * Navigations-Sidebar der Einstellungs- und Portalseiten auf der
 * Animated-Sidebar (`components/motion/animated-sidebar`): sie klappt per
 * Schalter in der Kopfzeile oder Strg/⌘+B ein, lässt dabei die Symbolleiste
 * (4.25rem) stehen und blendet nur die Beschriftungen aus; der aktive Eintrag
 * trägt eine Pille, die beim Wechsel an ihren neuen Platz gleitet. Mobil
 * erscheint dieselbe Sidebar als Blatt über dem Inhalt. Headings der
 * Items-Liste werden zu Gruppen-Labels; das Kundenlogo steht oben links.
 */
export function AppSidebar({
  items,
  selectedKey,
  setItem,
  aboveGroups,
  ...props
}: Properties & Omit<AnimatedSidebarProps, "children">) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [authentication] = useAtom<any>(authenticationAtom);
  /** Wo man steht, entscheidet, welche Bereichseinträge erscheinen. */
  const onDashboard = location.pathname === "/settings";
  const onCbos = location.pathname.startsWith("/cbos");
  // Fachprüfer (Plattformrolle) sehen „Regelwerke“ — gleich welchem Mandanten sie angehören.
  const [myPlatformRoles] = useAtom(myPlatformRolesAtom);
  const onRulebook = location.pathname === RULEBOOK_PATH;
  const { isMobile, open } = useAnimatedSidebar();
  const { logo: customerLogo, isFallback: isFallbackLogo } = useCustomerLogo();
  /**
   * Eingeklappt bleibt am Desktop die Symbolleiste stehen (`collapsible="icon"`).
   * Mobil gibt es sie nicht — dort ist die Sidebar ein Blatt, das entweder ganz
   * offen oder ganz weg ist.
   */
  const collapsed = !isMobile && !open;

  const groups = React.useMemo<SideBarGroupData[]>(() => {
    const result: SideBarGroupData[] = [];
    let current: SideBarGroupData | undefined;

    items.forEach((item) => {
      if (item.type === ItemType.heading) {
        current = { key: item.key, label: item.text, items: [] };
        result.push(current);
      } else {
        if (!current) {
          current = { key: "_top", items: [] };
          result.push(current);
        }
        current.items.push(item);
      }
    });

    return result;
  }, [items]);

  /**
   * Aufgeklappte Einträge mit Untermenü. Ein Eintrag, den man gerade geöffnet hat, steht offen
   * (auch über einen Link oder die Portalführung); der Knopf klappt ihn danach von Hand zu und auf.
   */
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});
  const isExpanded = (item: SideBarItem) => expanded[item.key] ?? selectedKey === item.key;
  React.useEffect(() => {
    if (!selectedKey) return;
    setExpanded((current) => (current[selectedKey] === false ? { ...current, [selectedKey]: true } : current));
  }, [selectedKey]);

  // Das Schließen des mobilen Blattes übernimmt der Menü-Knopf selbst.
  const selectItem = (item: SideBarItem) => {
    if (item.children?.length) {
      // Auf einem fremden Bereich öffnet der Klick ihn (mit seinem ersten Reiter) und klappt auf;
      // auf dem eigenen klappt er nur ein oder aus, der Inhalt bleibt stehen.
      if (selectedKey !== item.key) {
        setExpanded((current) => ({ ...current, [item.key]: true }));
        setItem?.(item.key);
        item.action?.();
      } else {
        setExpanded((current) => ({ ...current, [item.key]: !isExpanded(item) }));
      }
      return;
    }
    setItem?.(item.key);
    item.action?.();
  };

  return (
    <AnimatedSidebar collapsible="icon" variant="sidebar" {...props}>
      <AnimatedSidebarHeader className="gap-2 p-2">
        {/* Exakt so hoch wie die SiteHeader-Zeile (h-12) und ohne das
            Header-Padding, damit Logo/Schalter mit den Icons der Kopfzeile
            rechts auf einer Linie stehen. */}
        <div
          className={cn(
            "relative -mx-2 flex h-12 shrink-0 items-center gap-1 px-2",
            // Eine Wortmarke füllt die Zeile fast ganz aus. Ohne das negative
            // obere Außenmaß behält sie das Polster des Kopfbereichs über sich
            // und bekommt unten dasselbe dazu, statt zwischen Rand und erstem
            // Eintrag eingeklemmt zu sitzen.
            BRAND.logoIncludesName ? "mb-2" : "-mt-2",
          )}
        >
          {/* Kundenbranding oben links; das The-Real-Insight-Logo samt
              Namenszug ist der Fallback ohne Kundenlogo. Feste Höhe, Breite
              folgt dem Seitenverhältnis — so sitzt jedes Kundenlogo ohne
              Verzerrung in derselben Zeile.

              In der Symbolleiste blendet der Schriftzug aus und an seine
              Stelle tritt eine quadratische Logo-Kachel (linker Ausschnitt
              des Logos — dort sitzt die Bildmarke), zentriert auf der
              Icon-Achse der Einträge darunter. Beide Zustände überblenden
              nur ihre Deckkraft, die Zeile behält ihre Höhe. */}
          <button
            type="button"
            aria-hidden={collapsed}
            tabIndex={collapsed ? -1 : undefined}
            aria-label={t("header.toAssistant")}
            onClick={() => navigate("/chatbot")}
            className={cn(
              "flex min-w-0 flex-1 items-center gap-2 overflow-hidden rounded-md text-left outline-none transition-opacity duration-200",
              // Die Wortmarke beginnt mit einem Buchstabenstamm ohne eigene
              // Randzone — sie braucht mehr Einzug als das Monogramm, sonst
              // klebt sie am Rand der Leiste.
              BRAND.logoIncludesName ? "py-0 pl-3 pr-1.5" : "p-1.5",
              collapsed && "pointer-events-none opacity-0",
            )}
          >
            <img
              src={customerLogo}
              alt={isFallbackLogo && BRAND.logoIncludesName ? BRAND.name : ""}
              height={BRAND.logoIncludesName ? 44 : 32}
              data-testid="app-brand-logo"
              className={cn(
                "w-auto shrink-0 object-contain",
                // Die Kopfzeile ist h-12 (48px) und bleibt es — sie liegt auf
                // einer Linie mit der Kopfzeile rechts. 44px ist damit die
                // größte Marke, die noch mit etwas Luft hineinpasst.
                BRAND.logoIncludesName ? "h-11 max-w-[15rem]" : "h-8 max-w-[11rem]",
              )}
            />
            {isFallbackLogo && !BRAND.logoIncludesName ? (
              <span className="truncate text-base font-semibold">{BRAND.name}</span>
            ) : null}
          </button>
          <button
            type="button"
            aria-hidden={!collapsed}
            tabIndex={collapsed && !isMobile ? undefined : -1}
            aria-label={t("header.toAssistant")}
            onClick={() => navigate("/chatbot")}
            data-testid="app-brand-logo-square"
            className={cn(
              "absolute left-1/2 top-1/2 flex size-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center overflow-hidden rounded-lg border border-border/60 bg-background outline-none transition-opacity duration-200 focus-visible:ring-2 focus-visible:ring-ring",
              collapsed && !isMobile
                ? "opacity-100"
                : "pointer-events-none opacity-0",
            )}
          >
            {/* Ein Kundenlogo wird auf sein linkes Ende beschnitten — dort
                sitzt bei den meisten Lockups die Bildmarke. Eine Marke mit
                eigenem quadratischem Zeichen zeigt stattdessen dieses,
                vollständig und mit etwas Luft. */}
            <img
              src={isFallbackLogo ? BRAND.squareLogo : customerLogo}
              alt=""
              className={cn(
                "h-full w-full",
                isFallbackLogo ? "object-contain p-1" : "object-cover object-left",
              )}
            />
          </button>
          {/* Zuklappen oben rechts im Sidebar-Kopf — nur mobil (schließt das
              Blatt); am Desktop übernimmt der Schalter in der Kopfzeile. */}
          {isMobile ? (
            <AnimatedSidebarClose className="size-9 shrink-0 rounded-md text-sidebar-foreground transition-colors hover:bg-accent hover:text-accent-foreground [&_svg]:size-5">
              <SidebarCollapseIcon />
            </AnimatedSidebarClose>
          ) : null}
        </div>
        {/* Zurück zum Assistenten — normale Menüzeile wie die übrigen
            Einträge, nur mit dem Orb als Icon. `px-1` gleicht den Rand der
            Gruppen darunter aus (Content `px-2` + Group `px-1` gegen Header
            `p-2`), damit die Symbole in der Symbolleiste auf einer Achse
            stehen. */}
        <AnimatedSidebarMenu className="px-1">
          <AnimatedSidebarMenuItem>
            <AnimatedSidebarMenuButton
              icon={<MessageCircle size={16} />}
              onSelect={() => navigate("/chatbot")}
              className="border border-border/60"
            >
              {t("header.toAssistant")}
            </AnimatedSidebarMenuButton>
          </AnimatedSidebarMenuItem>
          {/*
            * Die Bereichseinträge zeigen nur dorthin, wo man **nicht** ist.
            *
            * Diese Seitenleiste trägt mehrere Bereiche: das Dashboard, die
            * Administration und CBOS. Ein Eintrag auf die eigene Seite führte
            * nirgendwohin — auf dem Dashboard fehlt deshalb „Zum Dashboard",
            * in CBOS fehlt „CBOS". Der Rang bleibt derselbe wie der des
            * Assistenten: eigener Bereich, keine Unterseite.
            */}
          {!onDashboard ? (
            <AnimatedSidebarMenuItem>
              <AnimatedSidebarMenuButton
                icon={<LayoutDashboard size={16} />}
                onSelect={() => navigate("/settings")}
                className="border border-border/60"
              >
                {t("header.toDashboard")}
              </AnimatedSidebarMenuButton>
            </AnimatedSidebarMenuItem>
          ) : null}
          {!onCbos && showsCbosEntry(authentication?.customer) ? (
            <AnimatedSidebarMenuItem>
              <AnimatedSidebarMenuButton
                icon={<Blocks size={16} />}
                onSelect={() => navigate(CBOS_ENTRY_PATH)}
                className="border border-border/60"
              >
                {t("header.cbos")}
              </AnimatedSidebarMenuButton>
            </AnimatedSidebarMenuItem>
          ) : null}
          {!onRulebook && myPlatformRoles && myPlatformRoles.length > 0 ? (
            <AnimatedSidebarMenuItem>
              <AnimatedSidebarMenuButton
                icon={<BookCheck size={16} />}
                onSelect={() => navigate(RULEBOOK_PATH)}
                className="border border-border/60"
              >
                {t("header.rulebook")}
              </AnimatedSidebarMenuButton>
            </AnimatedSidebarMenuItem>
          ) : null}
        </AnimatedSidebarMenu>
      </AnimatedSidebarHeader>
      <AnimatedSidebarContent>
        {aboveGroups ? (
          // In der Symbolleiste hat die Projektauswahl keine Breite — sie
          // entfällt dort. Grid-Trick statt `hidden`, damit die Gruppen
          // darunter beim Ein-/Ausklappen weich nachrücken.
          <div
            className={cn(
              "grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none",
              collapsed
                ? "pointer-events-none grid-rows-[0fr] opacity-0"
                : "grid-rows-[1fr] opacity-100",
            )}
            aria-hidden={collapsed}
          >
            <div className="min-h-0 overflow-hidden">
              <AnimatedSidebarGroup>{aboveGroups}</AnimatedSidebarGroup>
            </div>
          </div>
        ) : null}
        {groups.map((group) => (
          <AnimatedSidebarGroup key={group.key}>
            {group.label ? (
              <AnimatedSidebarGroupLabel>{group.label}</AnimatedSidebarGroupLabel>
            ) : null}
            <AnimatedSidebarGroupContent>
              <AnimatedSidebarMenu>
                {group.items.map((item) => {
                  const hasChildren = Boolean(item.children?.length);
                  const open = hasChildren && isExpanded(item);
                  return (
                    <AnimatedSidebarMenuItem key={item.key}>
                      <AnimatedSidebarMenuButton
                        icon={
                          React.isValidElement(item.icon)
                            ? React.cloneElement(item.icon as React.ReactElement<any>, {
                                size: 16,
                              })
                            : item.icon
                        }
                        // Mit Untermenü trägt der Reiter darunter die Markierung, nicht die Zeile.
                        isActive={!hasChildren && selectedKey === item.key}
                        ariaExpanded={hasChildren ? open : undefined}
                        onSelect={() => selectItem(item)}
                        // Guide id of each section (portal guide): the key is the
                        // same as `?panel=<key>`; headings have already become groups
                        // here and do not appear.
                        guideId={`nav.${item.key}`}
                      >
                        {String(item.text)}
                      </AnimatedSidebarMenuButton>
                      {hasChildren ? (
                        <AnimatedSidebarMenuSub open={open}>
                          {item.children!.map((child) => (
                            <AnimatedSidebarMenuSubItem key={child.key}>
                              <AnimatedSidebarMenuSubButton
                                isActive={child.active}
                                onSelect={child.action}
                                guideId={child.guideId}
                              >
                                {String(child.text)}
                              </AnimatedSidebarMenuSubButton>
                            </AnimatedSidebarMenuSubItem>
                          ))}
                        </AnimatedSidebarMenuSub>
                      ) : null}
                    </AnimatedSidebarMenuItem>
                  );
                })}
              </AnimatedSidebarMenu>
            </AnimatedSidebarGroupContent>
          </AnimatedSidebarGroup>
        ))}
      </AnimatedSidebarContent>
    </AnimatedSidebar>
  );
}
